import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import InfoTooltip from "@/components/ui/InfoTooltip";
import SearchInput from "@/components/ui/SearchInput";
import { TruncatedTextTooltip } from "@/components/ui/truncated-text-tooltip";
import { WidgetTitle } from "@/components/widgets/WidgetTitle";
import { TablePagingFooter } from "@/components/widgets/common/TablePagingFooter";
import { useColumnFilterState, ColFilterPopover } from "@/components/widgets/users/columnFilterShared";
import {
  fetchOverviewQueuePerformanceFilter,
  fetchOverviewQueuePerformanceTable,
  QueuePerformanceTableRow,
} from "@/lib/axios/api/widgets.api";
import { cn } from "@/lib/utils";
import { parseClockToSeconds } from "@/lib/tableGaugeMath";
import { MetricGaugeCell } from "@/components/widgets/common/MetricGaugeCell";
import { ChevronDown, ChevronUp, Download } from "lucide-react";
import * as XLSX from "xlsx";
import {
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableScrollContainer,
  TableEmptyRow,
  TABLE_CARD_CLASS,
  TABLE_HEADER_TITLE_CLASS,
  type TableColumnConfig,
} from "@/components/ui/table";

export type QueuePerformanceRow = QueuePerformanceTableRow;

const SEARCH_MAIN_COLUMN = "search";
const FILTER_PAGE_SIZE = 50;

/** Columns rendered as ledger-style gauge bars (max computed from current page rows). */
const GAUGE_COLUMN_KEYS = [
  "totalOfferedCalls",
  "answeredCall",
  "missedCall",
  "abandonedCall",
  "transferredCall",
  "voicemail",
  "averageHandleTime",
  "averageCallsPerAgent",
] as const;

function gaugeNumericValue(row: QueuePerformanceTableRow, key: (typeof GAUGE_COLUMN_KEYS)[number]): number {
  return key === "averageHandleTime" ? parseClockToSeconds(row.averageHandleTime) : Number(row[key] ?? 0);
}

function computeGaugeMaxima(rows: readonly QueuePerformanceTableRow[]): Record<string, number> {
  const out: Record<string, number> = {};
  if (!rows.length) return out;
  for (const key of GAUGE_COLUMN_KEYS) {
    out[key] = Math.max(...rows.map((r) => gaugeNumericValue(r, key)));
  }
  return out;
}

const BE_TO_UI_COLUMN: Record<string, string> = {
  name: "queueName",
  totalCall: "totalOfferedCalls",
  answered: "answeredCall",
  missed: "missedCall",
  abandoned: "abandonedCall",
  transferred: "transferredCall",
  voicemail: "voicemail",
  avgHandleTime: "averageHandleTime",
  avgCallsPerAgent: "averageCallsPerAgent",
};

const UI_TO_BE_COLUMN: Record<string, string> = Object.fromEntries(
  Object.entries(BE_TO_UI_COLUMN).map(([be, ui]) => [ui, be])
);

const columns: TableColumnConfig[] = [
  { title: "Name", key: "queueName", width: 200, filterable: true },
  { title: "Total Offered Calls", key: "totalOfferedCalls", width: 140, filterable: true },
  { title: "Answered", key: "answeredCall", width: 100, filterable: true },
  { title: "Missed", key: "missedCall", width: 100, filterable: true },
  { title: "Abandoned", key: "abandonedCall", width: 100, filterable: true },
  { title: "Transferred", key: "transferredCall", width: 110, filterable: true },
  { title: "Voicemail", key: "voicemail", width: 100, filterable: true },
  { title: "Avg. Handle Time", key: "averageHandleTime", width: 140, filterable: true },
  { title: "Avg Calls per Agent", key: "averageCallsPerAgent", width: 150, filterable: true },
];

function toBeColumn(uiColumn: string): string {
  return UI_TO_BE_COLUMN[uiColumn] ?? uiColumn;
}

function isAbortError(e: unknown): boolean {
  return e instanceof Error && (e.name === "CanceledError" || e.name === "AbortError");
}

function QueueNameCell({
  queueName,
  onQueueClick,
}: Readonly<{
  queueName: string;
  onQueueClick?: (queueName: string) => void;
}>) {
  return (
    <button
      type="button"
      className="cursor-pointer text-left w-full appearance-none bg-transparent p-0 border-0"
      onClick={() => onQueueClick?.(queueName)}
    >
      <TruncatedTextTooltip content={queueName} className="text-xs">
        {queueName}
      </TruncatedTextTooltip>
    </button>
  );
}

function columnFiltersToSingleText(values: readonly string[]): string {
  if (values.length === 0) return "";
  if (values.length === 1) return values[0] ?? "";
  return values.join(", ");
}

function buildOtherColumnFilters(columnFilters: Record<string, string[]>, uiCol: string) {
  return Object.entries(columnFilters)
    .filter(([col, vals]) => col !== uiCol && vals.length > 0)
    .map(([col, vals]) => {
      const text = columnFiltersToSingleText(vals);
      return { column: toBeColumn(col), value: text.length > 0 ? text : null };
    });
}

interface QueuePerformanceTableWidgetProps {
  startDate?: string;
  endDate?: string;
  queueMicrosoftIds?: string[] | null;
  onQueueClick?: (queueName: string) => void;
  queuesDataVersion?: number;
}

const QueuePerformanceTableWidget: React.FC<QueuePerformanceTableWidgetProps> = ({
  startDate,
  endDate,
  queueMicrosoftIds = null,
  onQueueClick,
  queuesDataVersion = 0,
}) => {
  const [sortKey, setSortKey] = useState("queueName");
  const [sortDir, setSortDir] = useState<"Asc" | "Desc">("Asc");
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
  const [tableData, setTableData] = useState<QueuePerformanceTableRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const colFilter = useColumnFilterState();
  const skeletonRowIds = useMemo(() => Array.from({ length: limit }, () => crypto.randomUUID()), [limit]);
  const gaugeMaxByKey = useMemo(() => computeGaugeMaxima(tableData), [tableData]);

  useEffect(() => {
    if (!startDate || !endDate) {
      setTableData([]);
      setTotal(0);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    const mainFilters = search ? [{ column: SEARCH_MAIN_COLUMN, value: search }] : null;

    setLoading(true);
    setError("");
    fetchOverviewQueuePerformanceTable(
      {
        startTime: startDate,
        endTime: endDate,
        queueMicrosoftIds,
        sorts: [{ column: sortKey, direction: sortDir }],
        pageIndex: page - 1,
        pageSize: limit,
        filters: columnFilters,
        mainFilters,
      },
      { signal: controller.signal }
    )
      .then((result) => {
        if (cancelled) return;
        setTableData(result.data);
        setTotal(result.total);
      })
      .catch((e) => {
        if (cancelled || isAbortError(e)) return;
        setError(e instanceof Error ? e.message : "Failed to load queue performance");
        setTableData([]);
        setTotal(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [startDate, endDate, queueMicrosoftIds, sortKey, sortDir, page, limit, columnFilters, search, queuesDataVersion]);

  useEffect(() => {
    setPage(1);
  }, [sortKey, sortDir, columnFilters, search, startDate, endDate, queueMicrosoftIds]);

  // Params that affect distinct values for OTHER columns' dropdowns (narrowing). On change, drop each
  // column's "loaded" marker (not its visible options) so the next open of any column re-fetches —
  // mirrors realtime's `clearFetchAndSessionState` on `listScopeKey` change.
  useEffect(() => {
    columns
      .filter((col) => col.filterable)
      .forEach((col) => colFilter.setEntry(col.key, { loadedPage: -1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, columnFilters]);

  useEffect(() => {
    colFilter.reset();
    setSearch("");
    setColumnFilters({});
    setSortKey("queueName");
    setSortDir("Asc");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, queueMicrosoftIds]);

  const fetchFilterPage = useCallback(
    async (uiCol: string, pageIndex: number, searchValue: string | null, append: boolean) => {
      if (!startDate || !endDate) return;
      colFilter.abortCol(uiCol);
      const ac = new AbortController();
      colFilter.abortRefs.current[uiCol] = ac;

      const beCol = toBeColumn(uiCol);
      const otherFilters = buildOtherColumnFilters(columnFilters, uiCol);
      const mainFilters = search ? [{ column: SEARCH_MAIN_COLUMN, value: search }] : null;

      try {
        const result = await fetchOverviewQueuePerformanceFilter(
          {
            startTime: startDate,
            endTime: endDate,
            queueMicrosoftIds,
            targetColumns: [beCol],
            pageIndex,
            pageSize: FILTER_PAGE_SIZE,
            mainFilters,
            filters: [...otherFilters, { column: beCol, value: searchValue }],
          },
          { signal: ac.signal }
        );
        if (ac.signal.aborted) return;
        const payload = result[beCol] ?? { values: [], total: 0 };
        colFilter.setEntry(uiCol, (prev) => {
          const base = append ? prev.options : [];
          const merged = [...new Set([...base, ...payload.values])];
          return {
            options: merged,
            hasMore: merged.length < payload.total,
            loadedPage: pageIndex,
            loading: false,
            searchQuery: prev.searchQuery,
          };
        });
      } catch (e) {
        if (ac.signal.aborted || isAbortError(e)) return;
        colFilter.setEntry(uiCol, { loading: false });
      }
    },
    [startDate, endDate, queueMicrosoftIds, search, columnFilters, colFilter]
  );

  // Fires on every dropdown open (`ColFilterPopover.handleOpenChange`): fetches this column's distinct
  // values unless already loaded for the current scope (mirrors realtime `ensureColumn`).
  const handleFilterOpen = useCallback(
    async (uiCol: string) => {
      if (!startDate || !endDate) return;
      const entry = colFilter.getEntry(uiCol);
      if (entry.loadedPage >= 0 && entry.options.length > 0) return;

      colFilter.setEntry(uiCol, { loading: true, searchQuery: "", loadedPage: -1, options: [], hasMore: false });
      await fetchFilterPage(uiCol, 0, null, false);
    },
    [colFilter, fetchFilterPage, startDate, endDate]
  );

  // Drop this column's "loaded" marker on close so the next open always re-fetches
  // (mirrors realtime's `onClose` clearing `loadedBrowsePage`/`loadedSearchPage`).
  const handleFilterClose = useCallback((col: string) => {
    colFilter.abortCol(col);
    const timer = colFilter.searchTimers.current[col];
    if (timer) { clearTimeout(timer); delete colFilter.searchTimers.current[col]; }
    colFilter.setEntry(col, { loadedPage: -1 });
  }, [colFilter]);

  const handleFilterSearchChange = useCallback(
    (col: string, query: string) => {
      colFilter.setEntry(col, { searchQuery: query });
      const prev = colFilter.searchTimers.current[col];
      if (prev) clearTimeout(prev);
      colFilter.searchTimers.current[col] = setTimeout(() => {
        colFilter.setEntry(col, { loading: true, loadedPage: -1, options: [], hasMore: false });
        fetchFilterPage(col, 0, query.trim() || null, false);
      }, 400);
    },
    [colFilter, fetchFilterPage]
  );

  const handleFilterLoadMore = useCallback(
    (col: string) => {
      const entry = colFilter.getEntry(col);
      if (!entry.hasMore || entry.loading) return;
      colFilter.setEntry(col, { loading: true });
      const isSearch = entry.searchQuery.trim().length > 0;
      fetchFilterPage(col, entry.loadedPage + 1, isSearch ? entry.searchQuery.trim() : null, true);
    },
    [colFilter, fetchFilterPage]
  );

  const handleToggleFilterValue = useCallback((col: string, val: string) => {
    setColumnFilters((prev) => {
      const cur = prev[col] ?? [];
      const next = cur.includes(val) ? cur.filter((v) => v !== val) : [...cur, val];
      return next.length
        ? { ...prev, [col]: next }
        : Object.fromEntries(Object.entries(prev).filter(([k]) => k !== col));
    });
  }, []);

  const handleClearFilter = useCallback((col: string) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[col];
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((col: string, vals: string[]) => {
    if (vals.length === 0) return;
    setColumnFilters((prev) => ({ ...prev, [col]: vals }));
  }, []);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "Asc" ? "Desc" : "Asc"));
    } else {
      setSortKey(key);
      setSortDir("Asc");
    }
  };

  const handleExport = useCallback(async () => {
    if (!startDate || !endDate) return;
    setExportLoading(true);
    try {
      const mainFilters = search ? [{ column: SEARCH_MAIN_COLUMN, value: search }] : null;
      const result = await fetchOverviewQueuePerformanceTable({
        startTime: startDate,
        endTime: endDate,
        queueMicrosoftIds,
        sorts: [{ column: sortKey, direction: sortDir }],
        pageIndex: 0,
        pageSize: null,
        // Export ignores column filters by design — always exports the full dataset
        // for the current date range/queue scope, independent of on-screen column narrowing.
        filters: {},
        mainFilters,
      });
      const headers = columns.map((c) => c.title);
      const rows = result.data.map((row) =>
        columns.map((col) => String(row[col.key as keyof QueuePerformanceTableRow] ?? ""))
      );
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      ws["!cols"] = columns.map(() => ({ wch: 15 }));
      XLSX.utils.book_append_sheet(wb, ws, "Queue Performance");
      const d = new Date();
      const dateStr = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
      XLSX.writeFile(wb, `queue_performance_${dateStr}.csv`);
    } catch (e) {
      console.error("Queue performance export failed:", e);
    } finally {
      setExportLoading(false);
    }
  }, [startDate, endDate, queueMicrosoftIds, sortKey, sortDir, search]);

  const renderTableBody = () => {
    if (loading) {
      return skeletonRowIds.map((rowId) => (
        <tr key={rowId} className="h-[45px]">
          {columns.map((col) => (
            <td key={col.key} className="p-4 align-middle" style={{ width: col.width }}>
              <div className="h-4 w-full bg-gray-200 animate-pulse rounded" />
            </td>
          ))}
        </tr>
      ));
    }

    if (error || tableData.length === 0) {
      return (
        <TableEmptyRow colSpan={columns.length} height={160}>
          No queue data was found
        </TableEmptyRow>
      );
    }

    return tableData.map((row, idx) => (
      <TableRow key={`${row.queueName}-${idx}`} index={idx}>
        {columns.map((col) => (
          <TableCell key={col.key} index={idx} className="text-xs" style={{ width: col.width }}>
            {col.key === "queueName" ? (
              <QueueNameCell queueName={row.queueName} onQueueClick={onQueueClick} />
            ) : (
              <MetricGaugeCell
                displayText={String(row[col.key as keyof QueuePerformanceTableRow])}
                value={gaugeNumericValue(row, col.key as (typeof GAUGE_COLUMN_KEYS)[number])}
                maxValue={gaugeMaxByKey[col.key] ?? 0}
              />
            )}
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  return (
    <Card className={TABLE_CARD_CLASS}>
      <CardHeader className="p-0">
        <div className="flex flex-row items-center justify-between gap-2 p-2 border-b">
          <div className="flex items-center gap-2 min-w-0">
            <WidgetTitle className="truncate">Queue Performance</WidgetTitle>
            <InfoTooltip content="The list of all call queue showing their detail metrics such as Total incoming call, number of answered calls, number of missed calls, number of abandoned calls, number of transferred calls, number of call to voicemail, average handled time (AHT), average number of calls per agent within the given time." />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <SearchInput
              placeholder="Search queue..."
              onSearch={(v) => setSearch(v.trim())}
              initialValue={search}
              debounceMs={300}
            />
            <Button
              variant="outline"
              // eslint-disable-next-line no-restricted-syntax -- Export button chrome, not table row/header coloring; not part of the shared table style contract.
              className="h-8 text-xs flex items-center gap-1 hover:bg-brand-accent hover:border-[#00FEC1] hover:text-black transition-colors dark:bg-[#0C3451] dark:border-[#4A6B85] dark:text-white dark:hover:bg-brand-accent dark:hover:border-[#00FEC1] dark:hover:text-black"
              onClick={handleExport}
              disabled={exportLoading || !startDate || !endDate}
            >
              {exportLoading ? (
                <>
                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-3 w-3" />
                  Export
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0 flex-1 flex flex-col min-h-0">
          <div className="relative overflow-hidden flex-1 flex flex-col min-h-0">
            <TableScrollContainer scrollY={!loading && tableData.length > 0}>
              <Table>
                <TableHeader>
                  <TableHeaderRow>
                    {columns.map((col) => (
                      <TableHeaderCell key={col.key} style={{ width: col.width }}>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            className="flex items-center gap-1 cursor-pointer"
                            onClick={() => handleSort(col.key)}
                          >
                            <span className={cn("whitespace-nowrap truncate text-xs", TABLE_HEADER_TITLE_CLASS)}>{col.title}</span>
                            {sortKey === col.key && (
                              sortDir === "Asc"
                                ? <ChevronUp className="h-3 w-3 flex-shrink-0" />
                                : <ChevronDown className="h-3 w-3 flex-shrink-0" />
                            )}
                          </button>
                          {col.filterable && (
                            <ColFilterPopover
                              columnKey={col.key}
                              selectedValues={columnFilters[col.key] ?? []}
                              entry={colFilter.getEntry(col.key)}
                              onOpen={() => handleFilterOpen(col.key)}
                              onClose={() => handleFilterClose(col.key)}
                              onToggleValue={handleToggleFilterValue}
                              onSearchChange={handleFilterSearchChange}
                              onLoadMore={handleFilterLoadMore}
                              onClearFilter={handleClearFilter}
                              onSelectAll={handleSelectAll}
                            />
                          )}
                        </div>
                      </TableHeaderCell>
                    ))}
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>{renderTableBody()}</TableBody>
              </Table>
            </TableScrollContainer>
          </div>
          <TablePagingFooter
            currentPage={page}
            totalPages={Math.max(1, Math.ceil(total / limit))}
            total={total}
            pageSize={limit}
            onPageChange={setPage}
            isLoading={loading}
            pageSizeOptions={[5, 10, 20, 50]}
            onPageSizeChange={(size) => {
              setLimit(size);
              setPage(1);
            }}
          />
      </CardContent>
    </Card>
  );
};

export default QueuePerformanceTableWidget;
